import type { Query, Schema } from '@rocicorp/zero';
import { WORKOUT_STATUSES } from '@strength/domain';
import z from 'zod';
import { defineOrgQuery } from '../shared/procedures';

/**
 * The tables whose rows key on a block — every query below roots on one of
 * them, so the per-block access guard can run through the `block` -> `track`
 * relationships in ZQL.
 */
type BlockKeyedTable =
  | 'AthletePublishedWorkoutLayer'
  | 'BlockPrintTask'
  | 'BlockPublishTask'
  | 'BlockPublishedWorkoutLayer';

/**
 * SC-5606: the per-block access rule every block-keyed publish/print read
 * applies server-side. Mirrors the loco-sync bootstrap authorization
 * (libs/trpc-server/src/lib/sync/authorize-sync-groups.ts): loco-sync never
 * grants org-wide block visibility — `TeamCoach_BlockBuilder` only for a
 * block on one of the coach's teams, `CrossTeamBlock_Readonly` only when the
 * coach manages an athlete enrolled in the block's track
 * (coachHasAthleteInBlock). Expressed in ZQL so a team-restricted coach
 * cannot read another team's published layers or print-task pdfUrl by
 * passing arbitrary block ids: the row's block is on one of the session's
 * teams, OR an athlete with a live enrollment in the block's track also has
 * a live enrollment on a track of one of the session's teams. All-access
 * coaches (`teamIds === null`) pass unfiltered.
 */
function withBlockAccess<TTable extends BlockKeyedTable, TReturn>(
  q: Query<TTable, Schema, TReturn>,
  teamIds: string[] | null,
): Query<TTable, Schema, TReturn> {
  if (teamIds === null) {
    return q;
  }
  return q.where(({ or, exists }) =>
    or(
      // The block's track is on one of the session's teams.
      exists('block', (block) =>
        block.whereExists('track', (track) =>
          track.where('teamId', 'IN', teamIds),
        ),
      ),
      // Or the coach manages an athlete enrolled in the block's track: a
      // live TrackAthlete row on that track whose profile also holds a live
      // TrackAthlete row on a coach-team track — the same join shape as
      // coachHasAthleteInBlock.
      exists('block', (block) =>
        block.whereExists('track', (track) =>
          track.whereExists('athletes', (athlete) =>
            athlete
              .where('deletedAt', 'IS', null)
              .whereExists('profile', (profile) =>
                profile.whereExists('trackAthletes', (enrollment) =>
                  enrollment
                    .where('deletedAt', 'IS', null)
                    .whereExists('track', (enrolledTrack) =>
                      enrolledTrack.where('teamId', 'IN', teamIds),
                    ),
                ),
              ),
          ),
        ),
      ),
    ),
  );
}

/**
 * The Zero read path for the Publish and print slice (SC-5503, SC-5606).
 * Plain row reads mirroring the loco-sync query shapes — the client
 * derivations stay client-side, and no relationship includes are needed for
 * this slice's consumers (block builder, calendar). Every query takes
 * validated input, requires at least one block id (callers pass a falsy
 * query, not an empty list, while their block list loads), and layers the
 * per-block access guard from `withBlockAccess` on top of the org scoping
 * `orgQuery` provides.
 */
export const publishQueries = {
  /**
   * The block's publish task, single row or undefined — `.one()` matches the
   * loco-sync `useQueryOne` reads the publish-state machine consumes.
   */
  publishTaskForBlock: defineOrgQuery(
    z.object({ blockId: z.uuid() }),
    ({ args, ctx, orgQuery }) =>
      withBlockAccess(
        orgQuery('BlockPublishTask').where('blockId', '=', args.blockId),
        ctx.teamIds,
      ).one(),
  ),
  /**
   * The block's print tasks; the optional `id` filter serves the
   * watch-by-task-id read the PDF export flow does via useQueryOne today.
   */
  printTasksForBlock: defineOrgQuery(
    z.object({
      blockIds: z.array(z.uuid()).min(1),
      id: z.uuid().optional(),
    }),
    ({ args, ctx, orgQuery }) => {
      let q = withBlockAccess(
        orgQuery('BlockPrintTask').where('blockId', 'IN', args.blockIds),
        ctx.teamIds,
      );
      if (args.id) {
        q = q.where('id', '=', args.id);
      }
      return q;
    },
  ),
  /**
   * Published workout layers for the requested blocks — the calendar's
   * all-athletes view (blockIds IN, isCurrent, statuses IN) and the block
   * builder's published-layer reads.
   */
  blockPublishedLayers: defineOrgQuery(
    z.object({
      blockIds: z.array(z.uuid()).min(1),
      isCurrent: z.boolean().optional(),
      statuses: z.array(z.enum(WORKOUT_STATUSES)).optional(),
    }),
    ({ args, ctx, orgQuery }) => {
      let q = withBlockAccess(
        orgQuery('BlockPublishedWorkoutLayer').where(
          'blockId',
          'IN',
          args.blockIds,
        ),
        ctx.teamIds,
      );
      if (args.isCurrent !== undefined) {
        q = q.where('isCurrent', '=', args.isCurrent);
      }
      if (args.statuses) {
        q = q.where('status', 'IN', args.statuses);
      }
      return q;
    },
  ),
  /**
   * Athletes' published workout layers for the requested blocks — covers the
   * block builder (blockIds IN, isCurrent) and the calendar (blockIds IN,
   * profileIds IN, isCurrent, statuses IN).
   */
  athletePublishedLayers: defineOrgQuery(
    z.object({
      blockIds: z.array(z.uuid()).min(1),
      profileIds: z.array(z.uuid()).optional(),
      isCurrent: z.boolean().optional(),
      statuses: z.array(z.enum(WORKOUT_STATUSES)).optional(),
    }),
    ({ args, ctx, orgQuery }) => {
      let q = withBlockAccess(
        orgQuery('AthletePublishedWorkoutLayer').where(
          'blockId',
          'IN',
          args.blockIds,
        ),
        ctx.teamIds,
      );
      if (args.profileIds) {
        q = q.where('profileId', 'IN', args.profileIds);
      }
      if (args.isCurrent !== undefined) {
        q = q.where('isCurrent', '=', args.isCurrent);
      }
      if (args.statuses) {
        q = q.where('status', 'IN', args.statuses);
      }
      return q;
    },
  ),
};
